import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { auth } from '@/lib/ezhard';
import type { User } from '@ezhard/sdk';

/* ─── Context shape ─── */
interface AuthContextValue {
  /** The currently authenticated user, or null if signed out. */
  user: User | null;
  /** True while the initial auth state is being resolved. */
  loading: boolean;
  /** Non-null if auth state observation failed. */
  error: Error | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/* ─── Provider ─── */
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // onAuthStateChanged returns an unsubscribe function directly
    const unsubscribe = auth.onAuthStateChanged((nextUser) => {
      setUser(nextUser);
      setLoading(false);
      setError(null);
      
      if (nextUser) {
        import('@/lib/auth')
          .then(({ ensureUserDoc }) => ensureUserDoc(nextUser))
          .catch((err) => console.error('Failed to sync user doc:', err));
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
}

/* ─── Hook ─── */
/**
 * Returns the current auth state.
 * Must be used within an AuthProvider.
 */
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}
